import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  limit,
  orderBy,
  startAfter,
  arrayUnion,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useToast } from '../components/Toast';
import { logger } from '../utils/logger';

const LeadsContext = createContext(null);

const LEADS_PAGE_SIZE = 50;

export function LeadsProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();
  const { showToast } = useToast();

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsLastDoc, setLeadsLastDoc] = useState(null);
  const [hasMoreLeads, setHasMoreLeads] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (!firebaseUser) return;
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;

    const q = query(
      collection(db, 'leads'),
      where('userId', '==', ownerIdToUse),
      orderBy('createdAt', 'desc'),
      limit(LEADS_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const leadsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLeads(leadsData);
        setLeadsLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMoreLeads(snapshot.docs.length >= LEADS_PAGE_SIZE);
        setLeadsLoading(false);
      },
      (error) => {
        logger.error('Error loading leads:', error);
        setLeadsLoading(false);
        showToast('Failed to load leads. Please refresh the page.', 'error');
      }
    );

    return unsubscribe;
  }, [firebaseUser, teamOwnerId, showToast]);

  const loadMoreLeads = async () => {
    if (!leadsLastDoc || !hasMoreLeads) return;
    try {
      const ownerIdToUse = teamOwnerId || firebaseUser.uid;
      const q = query(
        collection(db, 'leads'),
        where('userId', '==', ownerIdToUse),
        orderBy('createdAt', 'desc'),
        startAfter(leadsLastDoc),
        limit(LEADS_PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const moreData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLeads((prev) => [...prev, ...moreData]);
      setLeadsLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMoreLeads(snapshot.docs.length >= LEADS_PAGE_SIZE);
    } catch (error) {
      logger.error('Error loading more leads:', error);
      showToast('Failed to load more leads. Please try again.', 'error');
    }
  };

  const addLeadToFirebase = async (leadData) => {
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;
    try {
      const docRef = await addDoc(collection(db, 'leads'), {
        ...leadData,
        userId: ownerIdToUse,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      logger.error('Error adding lead:', error);
      return { success: false, error: error.message };
    }
  };

  const updateLeadInFirebase = async (leadId, updates) => {
    try {
      await updateDoc(doc(db, 'leads', leadId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      logger.error('Error updating lead:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteLeadFromFirebase = async (leadId) => {
    try {
      await deleteDoc(doc(db, 'leads', leadId));
      return { success: true };
    } catch (error) {
      logger.error('Error deleting lead:', error);
      return { success: false, error: error.message };
    }
  };

  // ── Activity Logging ──────────────────────────────────────────

  const appendActivityEntry = async (leadId, action, description) => {
    const entry = {
      id: `act-${Date.now()}`,
      action,
      description,
      timestamp: new Date().toISOString(),
      userName: firebaseUser?.displayName || firebaseUser?.email || 'System',
      editable: false,
    };
    try {
      await updateDoc(doc(db, 'leads', leadId), {
        activityLog: arrayUnion(entry),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.error('Error appending activity entry:', error);
    }
  };

  // ── Property data enrichment ───────────────

  /** Fetch property data and update lead — can be called independently of stage changes */
  const enrichLeadWithPropertyData = async (lead) => {
    if (!lead?.address || !lead?.city) return { success: false, error: 'Missing address or city' };
    try {
      const funcInstance = getFunctions();
      const propertyDetailFn = httpsCallable(funcInstance, 'propertyDetail');
      const cityParts = lead.city.split(', ');
      const cityName = cityParts[0] || lead.city;
      const stateName = cityParts[1] || lead.state || '';
      const address2 = [cityName, stateName, lead.zipCode].filter(Boolean).join(', ').trim();
      const attomResult = await propertyDetailFn({ address1: lead.address, address2 });
      if (attomResult.data.success) {
        const details = attomResult.data.data;
        const chars = details.property?.characteristics || {};
        const updates = {
          beds: chars.beds || lead.beds || '',
          baths: chars.baths || lead.baths || '',
          sqft: chars.sqft || lead.sqft || '',
          yearBuilt: chars.yearBuilt || '',
          lotSize: chars.lotSize || '',
          propertyType: chars.propertyType || lead.propertyType || 'Single Family',
          askingPrice: lead.askingPrice ?? (details.avm?.amount?.value || 0),
          estimatedArv: lead.estimatedArv ?? (details.avm?.amount?.value || 0),
          attomData: {
            avm: details.avm || null,
            assessment: details.property?.assessment || null,
            salesHistory: details.salesHistory || [],
            characteristics: chars,
            expandedProfile: details.expandedProfile || null,
            owner: details.property?.owner || null,
            property: details.property || null,
            fetchedAt: new Date().toISOString(),
          },
        };

        // Also fetch RentCast data (non-blocking — best-effort)
        try {
          const rentcastPropertyFn = httpsCallable(funcInstance, 'rentcastProperty');
          const rentcastValuationFn = httpsCallable(funcInstance, 'rentcastValuation');
          const rentcastRentFn = httpsCallable(funcInstance, 'rentcastRentEstimate');
          const rentcastMarketFn = httpsCallable(funcInstance, 'rentcastMarket');
          const rcAddress = [lead.address, lead.city, lead.state, lead.zipCode]
            .filter(Boolean)
            .join(', ');

          const [rcProp, rcVal, rcRent, rcMarket] = await Promise.allSettled([
            rentcastPropertyFn({ address: rcAddress }),
            rentcastValuationFn({ address: rcAddress }),
            rentcastRentFn({ address: rcAddress }),
            rentcastMarketFn({ zipCode: lead.zipCode }),
          ]);

          const rcData = {};
          if (rcProp.status === 'fulfilled' && rcProp.value?.data?.success)
            rcData.property = rcProp.value.data.data;
          if (rcVal.status === 'fulfilled' && rcVal.value?.data?.success)
            rcData.valuation = rcVal.value.data.data;
          if (rcRent.status === 'fulfilled' && rcRent.value?.data?.success)
            rcData.rentEstimate = rcRent.value.data.data;
          if (rcMarket.status === 'fulfilled' && rcMarket.value?.data?.success)
            rcData.market = rcMarket.value.data.data;

          if (Object.keys(rcData).length > 0) {
            updates.rentcastData = { ...rcData, fetchedAt: new Date().toISOString() };
          }
        } catch (rcErr) {
          logger.error('RentCast enrichment error (non-blocking):', rcErr);
        }

        await updateLeadInFirebase(lead.id, updates);
        return { success: true };
      }
      return { success: false, error: 'Property lookup returned no data' };
    } catch (err) {
      logger.error('enrichLeadWithPropertyData error:', err);
      return { success: false, error: err.message };
    }
  };

  // ── Deal Pipeline: Approval-chain functions ────────────────────

  // Move lead to underwriting stage + fire ATTOM fetch in background
  const submitForUnderwriting = async (lead) => {
    const result = await updateLeadInFirebase(lead.id, { stage: 'underwriting' });
    if (result.success)
      appendActivityEntry(lead.id, 'stage_change', 'Stage changed to Underwriting');
    if (result.success && lead.address && lead.city) {
      // Non-blocking ATTOM fetch to enrich lead with property data
      try {
        const funcInstance = getFunctions();
        const propertyDetail = httpsCallable(funcInstance, 'propertyDetail');
        const cityParts = lead.city.split(', ');
        const cityName = cityParts[0] || lead.city;
        const stateName = cityParts[1] || '';
        const address2 =
          `${cityName}${stateName ? ', ' + stateName : ''} ${lead.zipCode || ''}`.trim();
        const attomResult = await propertyDetail({ address1: lead.address, address2 });
        if (attomResult.data.success) {
          const details = attomResult.data.data;
          const chars = details.property?.characteristics || {};
          await updateLeadInFirebase(lead.id, {
            beds: chars.beds || lead.beds || '',
            baths: chars.baths || lead.baths || '',
            sqft: chars.sqft || lead.sqft || '',
            yearBuilt: chars.yearBuilt || '',
            lotSize: chars.lotSize || '',
            propertyType: chars.propertyType || lead.propertyType || 'Single Family',
            attomData: {
              avm: details.avm || null,
              assessment: details.property?.assessment || null,
              salesHistory: details.salesHistory || [],
              characteristics: chars,
              expandedProfile: details.expandedProfile || null,
              owner: details.property?.owner || null,
              fetchedAt: new Date().toISOString(),
            },
          });
        }
      } catch (err) {
        logger.error('Property data fetch after submitForUnderwriting:', err);
        // Non-blocking — lead stage was already updated successfully
      }
    }
    return result;
  };

  // Lock Flip Analyzer numbers and advance lead to cfo_review
  const submitForCFOReview = async (leadId, inputs, results, sensitivityScenarios) => {
    const lead = leads.find((l) => l.id === leadId);
    const ownerId = teamOwnerId || firebaseUser?.uid;

    // Push the about-to-be-overwritten lockedAnalysis onto the front of the
    // history array, cap at 3 entries. This preserves the v1/v2/v3 audit trail
    // for the Compare-to-last-locked-analyses card. Existing leads without a
    // lockedAnalysisHistory field default to an empty array.
    const previousLocked = lead?.lockedAnalysis;
    const previousHistory = Array.isArray(lead?.lockedAnalysisHistory)
      ? lead.lockedAnalysisHistory
      : [];
    const lockedAnalysisHistory = previousLocked
      ? [previousLocked, ...previousHistory].slice(0, 3)
      : previousHistory;

    const result = await updateLeadInFirebase(leadId, {
      stage: 'cfo_review',
      lockedAnalysis: {
        ...inputs,
        ...results,
        sensitivityScenarios: sensitivityScenarios || [],
        lockedAt: serverTimestamp(),
        lockedBy: firebaseUser?.uid || null,
        lockedByName: firebaseUser?.displayName || firebaseUser?.email || null,
      },
      lockedAnalysisHistory,
      cfoReview: {
        status: 'pending',
        reviewedBy: null,
        reviewedByName: null,
        reviewedAt: null,
        notes: '',
      },
    });

    if (result.success) appendActivityEntry(leadId, 'stage_change', 'Stage changed to CFO Review');

    // Create approval record for the Approvals page
    try {
      await addDoc(collection(db, 'approvals'), {
        type: 'cfo_review',
        status: 'pending',
        requestedBy: firebaseUser?.uid,
        requestedByName: firebaseUser?.displayName || firebaseUser?.email,
        requestedAt: serverTimestamp(),
        reviewedBy: null,
        reviewedByName: null,
        reviewedAt: null,
        reviewNotes: '',
        projectId: null,
        leadId: leadId,
        projectAddress: lead?.address || lead?.streetAddress || 'Unknown Address',
        ownerId,
        data: {
          leadId,
          leadAddress: lead?.address || lead?.streetAddress || '',
          purchasePrice: inputs.purchasePrice || 0,
          arv: inputs.arv || 0,
          rehabCost: inputs.rehabCost || 0,
          netProfit: results.profit || results.netProfit || 0,
          roi: results.roi || 0,
        },
      });
    } catch (err) {
      logger.error('Error creating CFO review approval record:', err);
    }

    return result;
  };

  // CFO approves → advance to ceo_approval
  const cfoApprove = async (leadId, notes) => {
    const lead = leads.find((l) => l.id === leadId);
    const ownerId = teamOwnerId || firebaseUser?.uid;

    const result = await updateLeadInFirebase(leadId, {
      stage: 'ceo_approval',

      cfoReview: {
        status: 'approved',
        reviewedBy: firebaseUser?.uid || null,
        reviewedByName: firebaseUser?.displayName || firebaseUser?.email || null,
        reviewedAt: serverTimestamp(),
        notes: notes || '',
      },
      ceoApproval: {
        status: 'pending',
        approvedBy: null,
        approvedByName: null,
        approvedAt: null,
        notes: '',
      },
    });

    if (result.success)
      appendActivityEntry(leadId, 'approval', 'CFO approved — advanced to CEO Approval');

    // Create CEO approval record for the Approvals page
    try {
      const locked = lead?.lockedAnalysis || {};
      await addDoc(collection(db, 'approvals'), {
        type: 'ceo_approval',
        status: 'pending',
        requestedBy: firebaseUser?.uid,
        requestedByName: firebaseUser?.displayName || firebaseUser?.email,
        requestedAt: serverTimestamp(),
        reviewedBy: null,
        reviewedByName: null,
        reviewedAt: null,
        reviewNotes: '',
        projectId: null,
        leadId: leadId,
        projectAddress: lead?.address || lead?.streetAddress || 'Unknown Address',
        ownerId,
        data: {
          leadId,
          leadAddress: lead?.address || lead?.streetAddress || '',
          purchasePrice: locked.purchasePrice || 0,
          arv: locked.arv || 0,
          rehabCost: locked.rehabCost || 0,
          netProfit: locked.profit || locked.netProfit || 0,
          roi: locked.roi || 0,
          cfoApprovedBy: firebaseUser?.displayName || firebaseUser?.email || '',
          cfoNotes: notes || '',
        },
      });
    } catch (err) {
      logger.error('Error creating CEO approval record:', err);
    }

    return result;
  };

  // CFO rejects → send lead back to identified
  const cfoReject = async (leadId, notes) => {
    const result = await updateLeadInFirebase(leadId, {
      stage: 'identified',
      cfoReview: {
        status: 'rejected',
        reviewedBy: firebaseUser?.uid || null,
        reviewedByName: firebaseUser?.displayName || firebaseUser?.email || null,
        reviewedAt: serverTimestamp(),
        notes: notes || '',
      },
    });
    if (result.success)
      appendActivityEntry(leadId, 'approval', 'CFO rejected — returned to Identified');
    return result;
  };

  // CEO approves → advance to funding_confirmed
  const ceoApprove = async (leadId, notes) => {
    const result = await updateLeadInFirebase(leadId, {
      stage: 'funding_confirmed',
      ceoApproval: {
        status: 'approved',
        approvedBy: firebaseUser?.uid || null,
        approvedByName: firebaseUser?.displayName || firebaseUser?.email || null,
        approvedAt: serverTimestamp(),
        notes: notes || '',
      },
      fundingConfirmation: {
        status: 'pending',
        confirmedBy: null,
        confirmedByName: null,
        confirmedAt: null,
        capitalSource: '',
        notes: '',
      },
    });
    if (result.success)
      appendActivityEntry(leadId, 'approval', 'CEO approved — advanced to Funding Confirmation');
    return result;
  };

  // CEO rejects → send lead back to identified
  const ceoReject = async (leadId, notes) => {
    const result = await updateLeadInFirebase(leadId, {
      stage: 'identified',
      ceoApproval: {
        status: 'rejected',
        approvedBy: firebaseUser?.uid || null,
        approvedByName: firebaseUser?.displayName || firebaseUser?.email || null,
        approvedAt: serverTimestamp(),
        notes: notes || '',
      },
    });
    if (result.success)
      appendActivityEntry(leadId, 'approval', 'CEO rejected — returned to Identified');
    return result;
  };

  // Confirm funding → advance to acquired
  const confirmFunding = async (leadId, capitalSource, notes) => {
    const result = await updateLeadInFirebase(leadId, {
      stage: 'acquired',
      fundingConfirmation: {
        status: 'confirmed',
        confirmedBy: firebaseUser?.uid || null,
        confirmedByName: firebaseUser?.displayName || firebaseUser?.email || null,
        confirmedAt: serverTimestamp(),
        capitalSource: capitalSource || '',
        notes: notes || '',
      },
    });
    if (result.success)
      appendActivityEntry(leadId, 'stage_change', 'Funding confirmed — lead acquired');
    return result;
  };

  // Move lead back one stage in the pipeline
  const STAGE_ORDER = [
    'identified',
    'underwriting',
    'inspection',
    'final_underwriting',
    'pending_purchase',
    'acquired',
  ];

  const moveLeadBack = async (leadId, currentStage) => {
    const currentIndex = STAGE_ORDER.indexOf(currentStage);
    if (currentIndex <= 0) return { success: false, error: 'Already at first stage' };

    const previousStage = STAGE_ORDER[currentIndex - 1];

    // Build update payload
    const updates = { stage: previousStage };

    const result = await updateLeadInFirebase(leadId, updates);
    if (result.success) {
      appendActivityEntry(
        leadId,
        'stage_change',
        `Moved back from ${currentStage.replace(/_/g, ' ')} to ${previousStage.replace(/_/g, ' ')}`
      );
    }
    return result;
  };

  // Duplicate a lead for strategy comparison (Flip vs BRRRR)
  const duplicateLeadForComparison = async (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return { success: false, error: 'Lead not found' };

    try {
      // Set original to flip mode if not already set
      if (!lead.analysisMode) {
        await updateLeadInFirebase(leadId, { analysisMode: 'flip' });
      }

      // Create a copy for BRRRR analysis
      const {
        id,
        createdAt,
        updatedAt,
        draftAnalysis,
        lockedAnalysis,
        cfoReview,
        ceoApproval,
        fundingConfirmation,
        projectId,
        ...copyData
      } = lead;
      const result = await addLeadToFirebase({
        ...copyData,
        analysisMode: 'brrrr',
        stage: lead.stage,
        comparisonSourceId: leadId,
      });

      if (result.success) {
        appendActivityEntry(leadId, 'comparison', 'Created BRRRR comparison copy');
      }
      return result;
    } catch (error) {
      logger.error('Error duplicating lead for comparison:', error);
      return { success: false, error: error.message };
    }
  };

  const value = useMemo(
    () => ({
      leads,
      setLeads,
      leadsLoading,
      addLeadToFirebase,
      updateLeadInFirebase,
      deleteLeadFromFirebase,
      selectedLead,
      setSelectedLead,
      loadMoreLeads,
      hasMoreLeads,
      enrichLeadWithPropertyData,
      // Deal pipeline approval-chain functions
      submitForUnderwriting,
      submitForCFOReview,
      cfoApprove,
      cfoReject,
      ceoApprove,
      ceoReject,
      confirmFunding,
      moveLeadBack,
      duplicateLeadForComparison,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [leads, leadsLoading, selectedLead, hasMoreLeads, firebaseUser]
  );

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
}

export function useLeads() {
  const context = useContext(LeadsContext);
  if (!context) throw new Error('useLeads must be used within LeadsProvider');
  return context;
}
